import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Wand } from 'lucide-react';
import BannerSpecificationsPanel from '@/components/admin/marketing/BannerSpecificationsPanel';
import BannerContentInput from '@/components/admin/marketing/BannerContentInput';
import BannerPreview from '@/components/admin/marketing/BannerPreview';
import { BannerSize, BannerTextLine } from '@/types/banner';
import { createBanner } from '@/utils/banner';
import { useAuth } from '@/context/SupabaseAuthContext';

const DEFAULT_SIZE: BannerSize = "medium_rectangle";
const MAX_GENERATION_ATTEMPTS = 5;

interface ImageSettings {
  width: number;
  opacity: number;
  position: { x: number, y: number };
}

const CreateBannerPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signOut } = useAuth();
  
  // Banner state
  const [selectedSize, setSelectedSize] = useState<BannerSize>(DEFAULT_SIZE);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [bannerId, setBannerId] = useState<string | null>(null);
  const [generationAttempt, setGenerationAttempt] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [borderStyle, setBorderStyle] = useState('none');
  const [borderWidth, setBorderWidth] = useState(1);
  const [borderColor, setBorderColor] = useState('#000000');
  const [imageSettings, setImageSettings] = useState<ImageSettings>({
    width: 80,
    opacity: 100,
    position: { x: 50, y: 50 }
  });
  
  // Multiple text lines support with positioned text
  const [textLines, setTextLines] = useState<BannerTextLine[]>([
    {
      text: 'Your Banner Text',
      fontFamily: 'Arial',
      fontSize: 24,
      color: '#000000',
      bold: false,
      italic: false,
      position: { x: 50, y: 30 }
    }
  ]);
  
  const handleImageUpload = (file: File) => {
    if (!file.name) {
      setImageFile(null);
      setImagePreviewUrl(null);
      return;
    }
    
    setImageFile(file);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };
  
  const handleCreateSample = async () => {
    if (textLines.length === 0 || textLines.every(line => !line.text)) {
      toast({
        title: "Missing content",
        description: "Please enter text for your banner.",
        variant: "destructive",
      });
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const banner = await createBanner(
        selectedSize,
        textLines,
        backgroundColor,
        imageFile || undefined,
        {
          borderStyle,
          borderWidth,
          borderColor,
          imageSettings
        }
      );
      
      setBannerId(banner.id);
      setGenerationAttempt(generationAttempt + 1);
      
      toast({
        title: "Banner created",
        description: `Banner #${banner.id} has been created successfully.`,
      });
    } catch (error) {
      console.error('Error creating banner:', error);
      toast({
        title: "Error",
        description: "Failed to create banner. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleGenerateNew = () => {
    if (generationAttempt >= MAX_GENERATION_ATTEMPTS) {
      toast({
        title: "Maximum attempts reached",
        description: "You've reached the maximum number of generation attempts.",
        variant: "destructive",
      });
      return;
    }
    
    setGenerationAttempt(generationAttempt + 1);
    handleCreateSample();
  };
  
  const handleApprove = () => {
    if (!bannerId) {
      toast({
        title: "No banner to approve",
        description: "Please create a banner sample first.",
        variant: "destructive",
      });
      return;
    }
    
    // Navigate to the manage banners page
    navigate('/admin/marketing/manage');
    
    toast({
      title: "Banner approved",
      description: `Banner #${bannerId} has been approved and saved as template.`,
    });
  };

  const handleImageWidthChange = (width: number) => {
    setImageSettings(prev => ({
      ...prev,
      width
    }));
  };

  const handleImageOpacityChange = (opacity: number) => {
    setImageSettings(prev => ({
      ...prev,
      opacity
    }));
  };

  const handleImagePositionChange = (position: { x: number, y: number }) => {
    setImageSettings(prev => ({
      ...prev,
      position
    }));
  };

  const handleTextPositionChange = (index: number, position: { x: number, y: number }) => {
    setTextLines(prevLines => 
      prevLines.map((line, i) => 
        i === index ? { ...line, position } : line
      )
    );
  };
  
  return (
    <AdminLayout isLoading={false} onLogout={signOut} userRole="admin">
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/marketing')}
              className="mb-2"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Marketing
            </Button>
            <h1 className="text-2xl font-bold">Create New Banner</h1>
            <p className="text-gray-500">
              Design and generate custom banners for your marketing campaigns
            </p>
          </div>
          
          <Button
            onClick={handleCreateSample}
            disabled={textLines.length === 0 || textLines.every(line => !line.text) || isGenerating}
            className="flex items-center bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <Wand className="mr-2 h-4 w-4" />
                Create Sample Banner
              </>
            )}
          </Button>
        </div>
        
        <Separator className="mb-6" />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Card className="overflow-hidden border-t-4 border-t-purple-500 shadow-md">
              <CardHeader className="bg-gray-50">
                <CardTitle>Banner Specifications</CardTitle>
              </CardHeader>
              <CardContent>
                <BannerSpecificationsPanel
                  selectedSize={selectedSize}
                  onSizeChange={setSelectedSize}
                />
              </CardContent>
            </Card>
            
            <Card className="overflow-hidden border-t-4 border-t-indigo-500 shadow-md">
              <CardHeader className="bg-gray-50">
                <CardTitle>Banner Content</CardTitle>
              </CardHeader>
              <CardContent>
                <BannerContentInput
                  textLines={textLines}
                  onTextLinesChange={setTextLines}
                  backgroundColor={backgroundColor}
                  onBackgroundColorChange={setBackgroundColor}
                  onImageUpload={handleImageUpload}
                  borderStyle={borderStyle}
                  onBorderStyleChange={setBorderStyle}
                  borderWidth={borderWidth}
                  onBorderWidthChange={setBorderWidth}
                  borderColor={borderColor}
                  onBorderColorChange={setBorderColor}
                  imageWidth={imageSettings.width}
                  onImageWidthChange={handleImageWidthChange}
                  imageOpacity={imageSettings.opacity}
                  onImageOpacityChange={handleImageOpacityChange}
                />
              </CardContent>
            </Card>
          </div>
          
          <div>
            <Card className="overflow-hidden border-t-4 border-t-blue-500 shadow-md">
              <CardHeader className="bg-gray-50">
                <CardTitle>Preview & Generation</CardTitle>
              </CardHeader>
              <CardContent>
                <BannerPreview
                  size={selectedSize}
                  textLines={textLines}
                  backgroundColor={backgroundColor}
                  imageUrl={imagePreviewUrl || undefined}
                  onGenerateNewVersion={handleGenerateNew}
                  onApprove={handleApprove}
                  currentAttempt={generationAttempt}
                  maxAttempts={MAX_GENERATION_ATTEMPTS}
                  bannerId={bannerId || undefined}
                  borderStyle={borderStyle}
                  borderWidth={borderWidth}
                  borderColor={borderColor}
                  imageSettings={imageSettings}
                  onImagePositionChange={handleImagePositionChange}
                  onTextPositionChange={handleTextPositionChange}
                />
                
                {generationAttempt >= MAX_GENERATION_ATTEMPTS && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
                    <p className="font-medium text-amber-700">Maximum attempts reached</p>
                    <p className="text-amber-600">
                      Please consider creating the banner offline using the specified size criteria.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CreateBannerPage;
